// lib/devicePreferences.ts
// The only door to localStorage for values scoped to this device (doctrine 08 §5). The key list
// is closed on purpose: a layout fit to one screen belongs here, anything personal belongs in
// User.preferences.

type DevicePreferenceValues = {
  // Studio bars: null until a gesture sets them, the bar then keeps its historical behaviour.
  studioLeftBarCollapsed: boolean | null;
  studioRightBarCollapsed: boolean | null;
  portalHomeMenuCollapsed: boolean;
  portalHomeMenuWidth: number | null;
};
type DevicePreferenceKey = keyof DevicePreferenceValues;

const isBoolean = (value: unknown) => typeof value === 'boolean';

const DEVICE_PREFERENCES: {
  [K in DevicePreferenceKey]: { storageKey: string; fallback: DevicePreferenceValues[K]; isValid: (value: unknown) => boolean };
} = {
  studioLeftBarCollapsed: { storageKey: 'fater-studio-left-bar-collapsed-v1', fallback: null, isValid: isBoolean },
  studioRightBarCollapsed: { storageKey: 'fater-studio-right-bar-collapsed-v1', fallback: null, isValid: isBoolean },
  portalHomeMenuCollapsed: { storageKey: 'fater-home-menu-collapsed-v1', fallback: false, isValid: isBoolean },
  // Key predates this module: renaming it would reset every stored width.
  portalHomeMenuWidth: {
    storageKey: 'fater-sidebar-width-v1',
    fallback: null,
    isValid: (value) => typeof value === 'number' && Number.isFinite(value) && value > 0,
  },
};

// Read once per tab, then kept in step by writeDevicePreference, so a render can read for free.
// Another tab's write lands on the next load, which is what a per-device layout wants anyway.
const cache = new Map<DevicePreferenceKey, unknown>();

export function readDevicePreference<K extends DevicePreferenceKey>(key: K): DevicePreferenceValues[K] {
  const { storageKey, fallback, isValid } = DEVICE_PREFERENCES[key];
  if (typeof window === 'undefined') return fallback;
  if (cache.has(key)) return cache.get(key) as DevicePreferenceValues[K];
  let value: DevicePreferenceValues[K] = fallback;
  try {
    const raw = window.localStorage.getItem(storageKey);
    const parsed: unknown = raw === null ? null : JSON.parse(raw);
    if (isValid(parsed)) value = parsed as DevicePreferenceValues[K];
  } catch {
    // A blocked storage or a corrupt value reads as the default.
  }
  cache.set(key, value);
  return value;
}

export function writeDevicePreference<K extends DevicePreferenceKey>(key: K, value: DevicePreferenceValues[K]): void {
  if (typeof window === 'undefined') return;
  cache.set(key, value);
  try {
    window.localStorage.setItem(DEVICE_PREFERENCES[key].storageKey, JSON.stringify(value));
  } catch {
    // Best-effort: a full or blocked storage only loses the memory across loads, never the gesture.
  }
}

// A Studio bar's memory only speaks on an open Studio project.
export const readStudioBarMemory = (
  key: 'studioLeftBarCollapsed' | 'studioRightBarCollapsed',
  openStudioProjectId: string | null,
): boolean | null => (openStudioProjectId ? readDevicePreference(key) : null);

// The memory wins over the surface's own rule.
export const resolveBarCollapse = (memory: boolean | null, fallback: boolean): boolean => memory ?? fallback;
